import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useShop } from "@/contexts/ShopContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, ExternalLink, RefreshCw, Search, FileText, Link2, AlertCircle, CheckCircle2, Rocket } from "lucide-react";
import { toast } from "sonner";

/**
 * Google Search Console integration page.
 *
 * Sections:
 * 1. Connect / Disconnect card (per shop)
 * 2. Site picker (which of the connected properties to query)
 * 3. Search analytics (top queries + top pages, last 28 days)
 * 4. URL Inspector (paste URL → indexing status)
 * 5. Sitemap manager (list + submit)
 */
export default function SearchConsole() {
  const { t, lang } = useLang();
  const { selectedShopId, selectedShop } = useShop();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();

  const shopId = selectedShopId;
  const shopDomain = selectedShop?.domain || "";
  const isDe = lang === "de";

  // If callback redirected here after successful OAuth, show a success toast
  const oauthStatus = searchParams.get("status");
  if (oauthStatus === "connected") {
    // Handled in the toast on first render; using searchParams as-is is safe here.
  }

  const { data: status, isLoading: statusLoading, refetch: refetchStatus } = useQuery({
    queryKey: ["gsc-status", shopId],
    queryFn: async () => (await api.get(`/gsc/status/${shopId}`)).data,
    enabled: !!shopId,
  });

  // Which property to query — defaults to first one that matches shop domain
  const [siteUrl, setSiteUrl] = useState("");
  const availableSites = status?.sites || [];
  const effectiveSite =
    siteUrl ||
    availableSites.find((s) => (s.siteUrl || "").includes(shopDomain))?.siteUrl ||
    availableSites[0]?.siteUrl ||
    "";

  const connectMutation = useMutation({
    mutationFn: async () => (await api.get(`/gsc/oauth/start?shop_id=${shopId}`)).data,
    onSuccess: (data) => {
      if (data.auth_url) window.location.href = data.auth_url;
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Failed to start OAuth"),
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => (await api.post(`/gsc/disconnect/${shopId}`)).data,
    onSuccess: () => {
      toast.success(isDe ? "Verbindung getrennt" : "Disconnected");
      qc.invalidateQueries({ queryKey: ["gsc-status", shopId] });
    },
  });

  return (
    <div className="space-y-6 rk-fade-in" data-testid="gsc-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">
          {shopDomain}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
          Google Search Console
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isDe
            ? "Echte Suchdaten, Indexstatus und Sitemap-Verwaltung direkt aus Google."
            : "Real search data, indexing status and sitemap management straight from Google."}
        </p>
      </header>

      {/* Connect card */}
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              {status?.connected ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-[hsl(var(--chart-3))]" />
                  {isDe ? "Verbunden" : "Connected"}
                </>
              ) : (
                <>
                  <AlertCircle className="h-5 w-5 text-muted-foreground" />
                  {isDe ? "Noch nicht verbunden" : "Not connected"}
                </>
              )}
            </CardTitle>
            <CardDescription className="mt-1">
              {status?.connected
                ? isDe
                  ? `${availableSites.length} Property${availableSites.length === 1 ? "" : "s"} verfügbar. Sitemap-Submits + Read.`
                  : `${availableSites.length} property${availableSites.length === 1 ? "" : "s"} available. Read + sitemap submit.`
                : isDe
                ? "Verbinde diesen Shop mit deinem Google-Account, um echte GSC-Daten zu sehen und Sitemaps zu verwalten."
                : "Connect this shop to your Google account to see real GSC data and manage sitemaps."}
            </CardDescription>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {status?.connected ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchStatus()}
                  disabled={statusLoading}
                  data-testid="gsc-refresh-status"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${statusLoading ? "animate-spin" : ""}`} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => disconnectMutation.mutate()}
                  disabled={disconnectMutation.isPending}
                  data-testid="gsc-disconnect"
                >
                  {isDe ? "Trennen" : "Disconnect"}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => connectMutation.mutate()}
                disabled={connectMutation.isPending || !shopId}
                data-testid="gsc-connect-btn"
                className="gap-2"
              >
                {connectMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4" />
                )}
                {isDe ? "Mit Google verbinden" : "Connect with Google"}
              </Button>
            )}
          </div>
        </CardHeader>

        {status?.connected && availableSites.length > 0 && (
          <CardContent className="pt-0">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="text-sm font-medium">{isDe ? "Property:" : "Property:"}</label>
              <Select value={effectiveSite} onValueChange={setSiteUrl}>
                <SelectTrigger className="w-[380px]" data-testid="gsc-site-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableSites.map((s) => (
                    <SelectItem key={s.siteUrl} value={s.siteUrl}>
                      {s.siteUrl} · {s.permissionLevel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Data sections only if connected + a site is picked */}
      {status?.connected && effectiveSite && (
        <>
          <SearchAnalytics shopId={shopId} siteUrl={effectiveSite} isDe={isDe} />
          <UrlInspector shopId={shopId} siteUrl={effectiveSite} shopDomain={shopDomain} isDe={isDe} />
          <SitemapManager shopId={shopId} siteUrl={effectiveSite} shopDomain={shopDomain} isDe={isDe} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Search Analytics section
// ---------------------------------------------------------------------------
function SearchAnalytics({ shopId, siteUrl, isDe }) {
  const [dim, setDim] = useState("query");

  const { data, isLoading } = useQuery({
    queryKey: ["gsc-analytics", shopId, siteUrl, dim],
    queryFn: async () =>
      (
        await api.post(`/gsc/${shopId}/search-analytics`, {
          site_url: siteUrl,
          dimensions: [dim],
          row_limit: 25,
        })
      ).data,
    enabled: !!siteUrl,
  });

  const rows = data?.rows || [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Search className="h-5 w-5" />
              {isDe ? "Suchdaten (letzte 28 Tage)" : "Search performance (last 28 days)"}
            </CardTitle>
            <CardDescription>
              {isDe ? "Echte Klicks, Impressionen und Position aus GSC." : "Real clicks, impressions and position from GSC."}
            </CardDescription>
          </div>
          <Select value={dim} onValueChange={setDim}>
            <SelectTrigger className="w-[160px]" data-testid="gsc-dim-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="query">{isDe ? "Nach Suchbegriff" : "By query"}</SelectItem>
              <SelectItem value="page">{isDe ? "Nach Seite" : "By page"}</SelectItem>
              <SelectItem value="country">{isDe ? "Nach Land" : "By country"}</SelectItem>
              <SelectItem value="device">{isDe ? "Nach Gerät" : "By device"}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="py-10 flex items-center justify-center text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {isDe ? "Lade GSC-Daten…" : "Loading GSC data…"}
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground text-sm">
            {isDe ? "Keine Daten für diesen Zeitraum." : "No data for this period."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left py-2 pr-4">{dim}</th>
                  <th className="text-right py-2 px-3">{isDe ? "Klicks" : "Clicks"}</th>
                  <th className="text-right py-2 px-3">{isDe ? "Impress." : "Impressions"}</th>
                  <th className="text-right py-2 px-3">CTR</th>
                  <th className="text-right py-2 pl-3">Ø {isDe ? "Pos." : "Pos."}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-border/40 hover:bg-secondary/30">
                    <td className="py-2 pr-4 font-mono text-xs max-w-[520px] truncate" title={r.keys?.[0]}>
                      {r.keys?.[0] || "-"}
                    </td>
                    <td className="text-right py-2 px-3 font-semibold">{r.clicks?.toLocaleString(isDe ? "de-DE" : "en") ?? 0}</td>
                    <td className="text-right py-2 px-3">{r.impressions?.toLocaleString(isDe ? "de-DE" : "en") ?? 0}</td>
                    <td className="text-right py-2 px-3">{r.ctr ? `${(r.ctr * 100).toFixed(1)}%` : "0%"}</td>
                    <td className="text-right py-2 pl-3">{r.position ? r.position.toFixed(1) : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// URL Inspector section
// ---------------------------------------------------------------------------
function UrlInspector({ shopId, siteUrl, shopDomain, isDe }) {
  const [url, setUrl] = useState(shopDomain ? `https://${shopDomain}/` : "");
  const [result, setResult] = useState(null);

  const inspect = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/gsc/${shopId}/url-inspection`, {
          site_url: siteUrl,
          inspection_url: url,
          language_code: isDe ? "de-DE" : "en-US",
        })
      ).data,
    onSuccess: (data) => setResult(data),
    onError: (err) => toast.error(err?.response?.data?.detail?.slice?.(0, 200) || "URL Inspection failed"),
  });

  const idx = result?.inspectionResult?.indexStatusResult;
  const mob = result?.inspectionResult?.mobileUsabilityResult;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Link2 className="h-5 w-5" />
          {isDe ? "URL Inspektor" : "URL Inspector"}
        </CardTitle>
        <CardDescription>
          {isDe
            ? "Prüfe ob eine URL im Google-Index ist, wann sie zuletzt gecrawlt wurde und ob es Fehler gibt."
            : "Check if a URL is in the Google index, when it was last crawled and whether there are any errors."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://redseadreams.com/hotels"
            data-testid="gsc-inspect-url"
          />
          <Button
            onClick={() => inspect.mutate()}
            disabled={!url || inspect.isPending}
            data-testid="gsc-inspect-btn"
            className="gap-2"
          >
            {inspect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {isDe ? "Prüfen" : "Inspect"}
          </Button>
        </div>

        {idx && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <StatCell label={isDe ? "Verdikt" : "Verdict"} value={idx.verdict} accent={idx.verdict === "PASS"} />
            <StatCell label={isDe ? "Coverage" : "Coverage"} value={idx.coverageState} />
            <StatCell label={isDe ? "Robots.txt" : "Robots.txt"} value={idx.robotsTxtState} />
            <StatCell label={isDe ? "Indexierbarkeit" : "Indexable"} value={idx.indexingState} />
            <StatCell label={isDe ? "Letzter Crawl" : "Last crawl"} value={idx.lastCrawlTime?.slice?.(0, 10) || "-"} />
            <StatCell label={isDe ? "Crawled als" : "Crawled as"} value={idx.crawledAs} />
            <StatCell label={isDe ? "Sitemap" : "Sitemap"} value={idx.sitemap?.[0] || "-"} />
            <StatCell label={isDe ? "Referrer" : "Referrer"} value={idx.referringUrls?.[0]?.slice?.(0, 40) || "-"} />
          </div>
        )}
        {mob && (
          <div className="text-xs text-muted-foreground">
            {isDe ? "Mobile-Nutzbarkeit:" : "Mobile usability:"}{" "}
            <span className={mob.verdict === "PASS" ? "text-[hsl(var(--chart-3))]" : "text-[hsl(var(--chart-1))]"}>
              {mob.verdict}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatCell({ label, value, accent }) {
  return (
    <div className="border border-border rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 font-mono text-xs truncate ${accent ? "text-[hsl(var(--chart-3))]" : "text-foreground"}`} title={String(value ?? "")}>
        {value ?? "-"}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sitemap Manager section
// ---------------------------------------------------------------------------
function SitemapManager({ shopId, siteUrl, shopDomain, isDe }) {
  const qc = useQueryClient();
  const [newSitemap, setNewSitemap] = useState(shopDomain ? `https://${shopDomain}/sitemap.xml` : "");

  const { data, isLoading } = useQuery({
    queryKey: ["gsc-sitemaps", shopId, siteUrl],
    queryFn: async () => (await api.get(`/gsc/${shopId}/sitemaps?site_url=${encodeURIComponent(siteUrl)}`)).data,
    enabled: !!siteUrl,
  });

  const submitMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/gsc/${shopId}/sitemaps/submit`, {
          site_url: siteUrl,
          sitemap_url: newSitemap,
        })
      ).data,
    onSuccess: () => {
      toast.success(isDe ? "Sitemap eingereicht — Google crawlt in Kürze" : "Sitemap submitted — Google will crawl shortly");
      qc.invalidateQueries({ queryKey: ["gsc-sitemaps", shopId, siteUrl] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail?.slice?.(0, 200) || "Submit failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (url) => (await api.post(`/gsc/${shopId}/sitemaps/delete`, { site_url: siteUrl, sitemap_url: url })).data,
    onSuccess: () => {
      toast.success(isDe ? "Sitemap entfernt" : "Sitemap removed");
      qc.invalidateQueries({ queryKey: ["gsc-sitemaps", shopId, siteUrl] });
    },
  });

  const sitemaps = data?.sitemap || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <FileText className="h-5 w-5" />
          {isDe ? "Sitemaps" : "Sitemaps"}
        </CardTitle>
        <CardDescription>
          {isDe
            ? "Verwalte submittete Sitemaps. Sitemap neu einreichen sagt Google \"crawl mich jetzt\" (schnellste legale Methode für Re-Indexierung)."
            : "Manage submitted sitemaps. Resubmitting a sitemap tells Google \"crawl me now\" (fastest legal way for re-indexing)."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            value={newSitemap}
            onChange={(e) => setNewSitemap(e.target.value)}
            placeholder="https://redseadreams.com/sitemap.xml"
            data-testid="gsc-sitemap-url"
          />
          <Button
            onClick={() => submitMutation.mutate()}
            disabled={!newSitemap || submitMutation.isPending}
            data-testid="gsc-sitemap-submit"
            className="gap-2"
          >
            {submitMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            {isDe ? "Einreichen / Ping" : "Submit / Ping"}
          </Button>
        </div>

        {isLoading ? (
          <div className="py-6 flex items-center justify-center text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {isDe ? "Lade Sitemaps…" : "Loading sitemaps…"}
          </div>
        ) : sitemaps.length === 0 ? (
          <div className="py-6 text-center text-muted-foreground text-sm">
            {isDe ? "Keine Sitemaps eingereicht." : "No sitemaps submitted."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left py-2 pr-4">Sitemap</th>
                  <th className="text-right py-2 px-3">{isDe ? "Zuletzt" : "Last"}</th>
                  <th className="text-right py-2 px-3">{isDe ? "Warnungen" : "Warnings"}</th>
                  <th className="text-right py-2 px-3">{isDe ? "Fehler" : "Errors"}</th>
                  <th className="text-right py-2 pl-3"></th>
                </tr>
              </thead>
              <tbody>
                {sitemaps.map((s) => (
                  <tr key={s.path} className="border-b border-border/40">
                    <td className="py-2 pr-4 font-mono text-xs max-w-[420px] truncate" title={s.path}>{s.path}</td>
                    <td className="text-right py-2 px-3 text-xs">{s.lastSubmitted?.slice(0, 10) || "-"}</td>
                    <td className="text-right py-2 px-3">{s.warnings ?? 0}</td>
                    <td className={`text-right py-2 px-3 ${Number(s.errors) > 0 ? "text-[hsl(var(--chart-1))]" : ""}`}>{s.errors ?? 0}</td>
                    <td className="text-right py-2 pl-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteMutation.mutate(s.path)}
                        disabled={deleteMutation.isPending}
                        data-testid={`gsc-sitemap-delete-${s.path}`}
                      >
                        ×
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
